/**
 * Report data query layer using Gen 2 typed client.
 *
 * Provides fetch functions for InspectionReport, DailyServiceReport,
 * legacy Report, ReportAnswer, ExpenseRecord, PMReport, and TireReport records.
 * Includes legacy normalization, merge/sort/deduplicate, and CRUD for
 * SavedReportConfiguration and UserReportFavorite.
 */
import { authClient } from "../utils/amplifyClient";
import type { Report } from "../types/schema";
import { KEYS_START_OF_DAY_REPORT } from "../utils/constants";
import type { NormalizedReportRecord } from "../types/reportTypes";
import { computeSummaryCounts, computeDurationSeconds } from "../utils/reportUtils";
import {
    buildUtilizationReportRecords,
    type UtilizationUnitSource,
} from "../utils/utilizationReportMetrics";
import { fetchExpenseRecordsByCompany } from "./costTrackingQueries";
import {
    listInspectionReportByCompanyIdAndCreatedAt,
    listDailyServiceReportByCompanyIdAndCreatedAt,
    listShiftReportByCompanyIdAndCreatedAt,
} from "./queries";

// ---------------------------------------------------------------------------
// Filter criteria shared by the merged-report helpers
// ---------------------------------------------------------------------------

export interface ReportFilterCriteria {
    dateStart?: string;
    dateEnd?: string;
    equipmentCategoryId?: string;
    unitId?: string;
    locationId?: string;
    userId?: string;
}

function buildCreatedAtKeyCondition(filters?: { dateStart?: string; dateEnd?: string }) {
    if (filters?.dateStart && filters?.dateEnd) {
        return {
            between: [filters.dateStart, `${filters.dateEnd}T23:59:59.999Z`],
        };
    }
    if (filters?.dateStart) {
        return { ge: filters.dateStart };
    }
    if (filters?.dateEnd) {
        return { le: `${filters.dateEnd}T23:59:59.999Z` };
    }
    return { ge: '2000-01-01T00:00:00.000Z' };
}

async function paginateGraphqlList<T>(
    query: string,
    connectionField: string,
    variables: Record<string, unknown>,
): Promise<T[]> {
    const items: T[] = [];
    let nextToken: string | null | undefined = undefined;

    do {
        const response = (await authClient.graphql({
            query,
            variables: {
                ...variables,
                nextToken: nextToken ?? null,
                limit: 250,
                sortDirection: 'DESC',
            },
        })) as {
            data?: Record<string, { items?: T[]; nextToken?: string | null } | null>;
            errors?: Array<{ message?: string | null }>;
        };

        if (response.errors?.length) {
            throw new Error(response.errors.map((entry) => entry.message).filter(Boolean).join('; '));
        }

        const connection = response.data?.[connectionField];
        if (connection?.items?.length) {
            items.push(...connection.items);
        }
        nextToken = connection?.nextToken;
    } while (nextToken);

    return items;
}

// ---------------------------------------------------------------------------
// Paginated fetch helpers — new Phase 1 tables
// ---------------------------------------------------------------------------

export async function fetchInspectionReports(
    companyId: string,
    filters?: { dateStart?: string; dateEnd?: string; equipmentCategoryId?: string },
) {
    const createdAt = buildCreatedAtKeyCondition(filters);
    const items = await paginateGraphqlList<Record<string, unknown>>(
        listInspectionReportByCompanyIdAndCreatedAt,
        'listInspectionReportByCompanyIdAndCreatedAt',
        {
            companyId,
            createdAt,
            ...(filters?.equipmentCategoryId
                ? { filter: { equipmentCategoryId: { eq: filters.equipmentCategoryId } } }
                : {}),
        },
    );

    return items;
}

export async function fetchDailyServiceReports(
    companyId: string,
    filters?: { dateStart?: string; dateEnd?: string; equipmentCategoryId?: string },
) {
    const createdAt = buildCreatedAtKeyCondition(filters);
    return paginateGraphqlList<Record<string, unknown>>(
        listDailyServiceReportByCompanyIdAndCreatedAt,
        'listDailyServiceReportByCompanyIdAndCreatedAt',
        {
            companyId,
            createdAt,
            ...(filters?.equipmentCategoryId
                ? { filter: { equipmentCategoryId: { eq: filters.equipmentCategoryId } } }
                : {}),
        },
    );
}

// ---------------------------------------------------------------------------
// Paginated fetch — legacy Report table
// ---------------------------------------------------------------------------

export async function fetchLegacyReports(
    companyId: string,
    type: 'inspection' | 'dailyService',
    filters?: { dateStart?: string; dateEnd?: string },
): Promise<Report[]> {
    const { data } = await authClient.models.Report.reportsByCompanyId({
        companyId,
        ...(filters?.dateStart && filters?.dateEnd
            ? { createdAt: { between: [filters.dateStart, filters.dateEnd] } }
            : filters?.dateStart
                ? { createdAt: { ge: filters.dateStart } }
                : filters?.dateEnd
                    ? { createdAt: { le: filters.dateEnd } }
                    : {}),
    });

    if (type === 'dailyService') {
        return data.filter((r) => r.dailyServiceLogReport === true) as Report[];
    }
    return data.filter((r) => r.dailyServiceLogReport !== true) as Report[];
}

// ---------------------------------------------------------------------------
// Legacy normalization
// ---------------------------------------------------------------------------

export function normalizeLegacyReport(
    legacy: Report,
    _type: 'inspection' | 'dailyService',
): NormalizedReportRecord {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let prompts: any[] = [];
    try {
        prompts = JSON.parse(legacy.userData || '[]');
        if (!Array.isArray(prompts)) prompts = [];
    } catch {
        prompts = [];
    }

    const counts = computeSummaryCounts(prompts);
    const duration = computeDurationSeconds(legacy.inspectionStarted, legacy.inspectionCompleted);

    return {
        id: legacy.id,
        source: 'LegacyReport',
        companyId: legacy.companyId,
        equipmentCategoryId: legacy.equipmentId,
        equipmentCategoryName: legacy.equipmentName || '',
        unitId: legacy.unitId || undefined,
        unitName: legacy.unitName || undefined,
        userId: legacy.userId,
        userName: legacy.name,
        locationId: legacy.equipmentLocationId || undefined,
        okCount: counts.okCount,
        warnCount: counts.warnCount,
        inopCount: counts.inopCount,
        totalItems: counts.totalItems,
        inspectionDurationSeconds: duration ?? undefined,
        createdAt: legacy.createdAt || '',
        responseData: legacy.userData,
    };
}

// ---------------------------------------------------------------------------
// Merge, sort, deduplicate
// ---------------------------------------------------------------------------

export function mergeReportRecords(
    newRecords: NormalizedReportRecord[],
    legacyRecords: NormalizedReportRecord[],
): NormalizedReportRecord[] {
    const combined = [...newRecords, ...legacyRecords];
    combined.sort((a, b) => (b.createdAt > a.createdAt ? 1 : b.createdAt < a.createdAt ? -1 : 0));

    const seen = new Set<string>();
    const result: NormalizedReportRecord[] = [];
    for (const record of combined) {
        if (!seen.has(record.id)) {
            seen.add(record.id);
            result.push(record);
        }
    }
    return result;
}

// ---------------------------------------------------------------------------
// Normalize helpers
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeInspectionReport(report: any): NormalizedReportRecord {
    return {
        id: report.id,
        source: 'InspectionReport',
        companyId: report.companyId,
        equipmentCategoryId: report.equipmentCategoryId,
        equipmentCategoryName: report.equipmentCategoryName || '',
        unitId: report.unitId || undefined,
        unitName: report.unitName || undefined,
        userId: report.userId,
        userName: report.userName || '',
        locationId: report.locationId || undefined,
        okCount: report.okCount ?? 0,
        warnCount: report.warnCount ?? 0,
        inopCount: report.inopCount ?? 0,
        totalItems: report.totalItems ?? 0,
        inspectionDurationSeconds: report.inspectionDurationSeconds ?? undefined,
        createdAt: report.createdAt || report.inspectionCompleted || '',
        responseData: report.responseData || undefined,
    };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeDailyServiceReport(report: any): NormalizedReportRecord {
    return {
        id: report.id,
        source: 'DailyServiceReport',
        companyId: report.companyId,
        equipmentCategoryId: report.equipmentCategoryId,
        equipmentCategoryName: report.equipmentCategoryName || '',
        unitId: report.unitId || undefined,
        unitName: report.unitName || undefined,
        userId: report.userId,
        userName: report.userName || '',
        locationId: report.locationId || undefined,
        okCount: report.okCount ?? 0,
        warnCount: report.warnCount ?? 0,
        inopCount: report.inopCount ?? 0,
        totalItems: report.totalItems ?? 0,
        inspectionDurationSeconds: report.inspectionDurationSeconds ?? undefined,
        createdAt: report.createdAt || report.inspectionCompleted || '',
        responseData: report.responseData || undefined,
    };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeShiftReport(report: any): NormalizedReportRecord {
    return {
        id: report.id,
        source: 'ShiftReport',
        companyId: report.companyId,
        equipmentCategoryId: '',
        equipmentCategoryName: '',
        unitId: undefined,
        unitName: undefined,
        userId: report.userId,
        userName: report.userName || '',
        locationId: report.locationId || undefined,
        okCount: report.okCount ?? 0,
        warnCount: report.warnCount ?? 0,
        inopCount: report.inopCount ?? 0,
        totalItems: report.totalItems ?? 0,
        inspectionDurationSeconds: report.inspectionDurationSeconds ?? undefined,
        createdAt: report.createdAt,
        responseData: report.responseData || undefined,
        shiftType: report.shiftType || 'start-of-day',
    };
}

function normalizeLegacyStartOfDayReport(legacy: Report): NormalizedReportRecord {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let prompts: any[] = [];
    try {
        prompts = JSON.parse(legacy.userData || '[]');
        if (!Array.isArray(prompts)) prompts = [];
    } catch {
        prompts = [];
    }

    const counts = computeSummaryCounts(prompts);
    const duration = computeDurationSeconds(legacy.inspectionStarted, legacy.inspectionCompleted);

    return {
        id: legacy.id,
        source: 'LegacyReport',
        companyId: legacy.companyId,
        equipmentCategoryId: '',
        equipmentCategoryName: '',
        unitId: undefined,
        unitName: undefined,
        userId: legacy.userId,
        userName: legacy.name,
        locationId: legacy.equipmentLocationId || undefined,
        okCount: counts.okCount,
        warnCount: counts.warnCount,
        inopCount: counts.inopCount,
        totalItems: counts.totalItems,
        inspectionDurationSeconds: duration ?? undefined,
        createdAt: legacy.createdAt || '',
        responseData: legacy.userData,
        shiftType: 'start-of-day',
    };
}

// ---------------------------------------------------------------------------
// Merged fetch helpers
// ---------------------------------------------------------------------------

export async function fetchMergedInspectionReports(
    companyId: string,
    filters?: ReportFilterCriteria,
): Promise<NormalizedReportRecord[]> {
    const [newReports, legacyReports] = await Promise.all([
        fetchInspectionReports(companyId, {
            dateStart: filters?.dateStart,
            dateEnd: filters?.dateEnd,
            equipmentCategoryId: filters?.equipmentCategoryId,
        }),
        fetchLegacyReports(companyId, 'inspection', {
            dateStart: filters?.dateStart,
            dateEnd: filters?.dateEnd,
        }),
    ]);

    const normalizedNew = newReports.map(normalizeInspectionReport);
    const filteredLegacy = legacyReports.filter(
        (r) => r.equipmentId !== KEYS_START_OF_DAY_REPORT.EQUIPMENT_ID,
    );
    const normalizedLegacy = filteredLegacy.map((r) => normalizeLegacyReport(r, 'inspection'));

    return mergeReportRecords(normalizedNew, normalizedLegacy);
}

export async function fetchMergedDailyServiceReports(
    companyId: string,
    filters?: ReportFilterCriteria,
): Promise<NormalizedReportRecord[]> {
    const [newReports, legacyReports] = await Promise.all([
        fetchDailyServiceReports(companyId, {
            dateStart: filters?.dateStart,
            dateEnd: filters?.dateEnd,
            equipmentCategoryId: filters?.equipmentCategoryId,
        }),
        fetchLegacyReports(companyId, 'dailyService', {
            dateStart: filters?.dateStart,
            dateEnd: filters?.dateEnd,
        }),
    ]);

    const normalizedNew = newReports.map(normalizeDailyServiceReport);
    const normalizedLegacy = legacyReports.map((r) => normalizeLegacyReport(r, 'dailyService'));

    return mergeReportRecords(normalizedNew, normalizedLegacy);
}

export async function fetchShiftReports(
    companyId: string,
    filters?: { dateStart?: string; dateEnd?: string },
) {
    const createdAt = buildCreatedAtKeyCondition(filters);
    return paginateGraphqlList<Record<string, unknown>>(
        listShiftReportByCompanyIdAndCreatedAt,
        'listShiftReportByCompanyIdAndCreatedAt',
        { companyId, createdAt },
    );
}

export async function fetchMergedShiftReports(
    companyId: string,
    filters?: ReportFilterCriteria,
): Promise<NormalizedReportRecord[]> {
    const [newReports, legacyReports] = await Promise.all([
        fetchShiftReports(companyId, {
            dateStart: filters?.dateStart,
            dateEnd: filters?.dateEnd,
        }),
        fetchLegacyReports(companyId, 'inspection', {
            dateStart: filters?.dateStart,
            dateEnd: filters?.dateEnd,
        }),
    ]);

    const normalizedNew = newReports.map(normalizeShiftReport);
    const legacyStartOfDay = legacyReports.filter(
        (r) => r.equipmentId === KEYS_START_OF_DAY_REPORT.EQUIPMENT_ID,
    );
    const normalizedLegacy = legacyStartOfDay.map(normalizeLegacyStartOfDayReport);

    return mergeReportRecords(normalizedNew, normalizedLegacy);
}

// ---------------------------------------------------------------------------
// Additional report data fetchers
// ---------------------------------------------------------------------------

export async function fetchReportAnswers(
    companyId: string,
    filters?: { unitId?: string; promptKey?: string },
) {
    const { data } = await authClient.models.ReportAnswer.listReportAnswerByCompanyId({ companyId });
    let filtered = data;
    if (filters?.unitId) filtered = filtered.filter(r => r.unitId === filters.unitId);
    if (filters?.promptKey) filtered = filtered.filter(r => r.promptKey === filters.promptKey);
    return filtered;
}

export async function fetchExpenseRecords(companyId: string) {
    return fetchExpenseRecordsByCompany(companyId);
}

export async function fetchPMReports(companyId: string) {
    const { data } = await authClient.models.PMReport.listPMReportByCompanyIdAndCreatedAt({ companyId });
    return data;
}

export async function fetchTireReports(companyId: string) {
    const { data } = await authClient.models.TireReport.tireReportsByCompanyId({ companyId });
    return data;
}

/**
 * Per-unit rows for Utilization %, Projected vs Actual UM %, and Cost per UM.
 * Loads company units (via categories), expenses, and location names.
 */
export async function fetchUtilizationReportRecords(companyId: string) {
    const [{ data: categories }, expenses, { data: locations }] = await Promise.all([
        authClient.models.EquipmentCategory.listEquipmentCategoryByCompanyId({ companyId }),
        fetchExpenseRecordsByCompany(companyId),
        authClient.models.Location.listLocationByCompanyId({ companyId }),
    ]);

    const categoryNameById = new Map(categories.map((category) => [category.id, category.name]));
    const locationNameById = new Map(locations.map((location) => [location.id, location.name]));

    const unitResults = await Promise.all(
        categories.map((category) =>
            authClient.models.SingleUnit.listSingleUnitsByCategoryId({ categoryId: category.id }),
        ),
    );

    const units: UtilizationUnitSource[] = unitResults.flatMap((result) =>
        result.data.map((unit) => {
            const reading = unit as UtilizationUnitSource;
            return {
                id: unit.id,
                name: unit.name,
                categoryId: unit.categoryId,
                categoryName: unit.categoryId ? categoryNameById.get(unit.categoryId) ?? null : null,
                locationId: unit.locationId,
                locationName: unit.locationId ? locationNameById.get(unit.locationId) ?? null : null,
                putInServiceDate: unit.putInServiceDate,
                aquisitionDate: unit.aquisitionDate,
                inputDate: unit.inputDate,
                lastInspectionDate: unit.lastInspectionDate,
                lastDailyServiceDate: unit.lastDailyServiceDate,
                lastInspectionUMCounter: reading.lastInspectionUMCounter,
                lastDailyServiceUMCounter: reading.lastDailyServiceUMCounter,
                machineUMCounterAtAquisition: unit.machineUMCounterAtAquisition,
                currentUMCounterValue: unit.currentUMCounterValue,
                replacedUMCounterValue: unit.replacedUMCounterValue,
                annualProjectedUsage: unit.annualProjectedUsage,
                utilizationAdjustmentDays: unit.utilizationAdjustmentDays,
            };
        }),
    );

    return buildUtilizationReportRecords(
        units,
        expenses.map((expense) => ({
            unitId: expense.unitId,
            totalExpense: expense.totalExpense,
        })),
    );
}

// ---------------------------------------------------------------------------
// SavedReportConfiguration CRUD
// ---------------------------------------------------------------------------

export async function fetchSavedReportConfigs(companyId: string) {
    const { data } = await authClient.models.SavedReportConfiguration.listSavedReportConfigurationByCompanyIdAndCreatedAt({ companyId });
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createSavedReportConfig(input: any) {
    const { data } = await authClient.models.SavedReportConfiguration.create(input);
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateSavedReportConfig(input: any) {
    const { data } = await authClient.models.SavedReportConfiguration.update(input);
    return data;
}

export async function deleteSavedReportConfig(id: string) {
    const { data } = await authClient.models.SavedReportConfiguration.delete({ id });
    return data;
}

// ---------------------------------------------------------------------------
// UserReportFavorite CRUD
// ---------------------------------------------------------------------------

export async function fetchUserFavorites(userId: string, companyId: string) {
    const { data } = await authClient.models.UserReportFavorite.listUserReportFavoriteByUserIdAndReportTypeKey({ userId });
    return data.filter(f => f.companyId === companyId);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createUserFavorite(input: any) {
    const { data } = await authClient.models.UserReportFavorite.create(input);
    return data;
}

export async function deleteUserFavorite(id: string) {
    const { data } = await authClient.models.UserReportFavorite.delete({ id });
    return data;
}
